<template>
  <Transition name="fade">
    <div
      v-if="showing"
      class="fixed pt-24 pb-10 inset-0 w-full h-screen flex items-center justify-center bg-semi-75 z-20"
    >
      <div
        class="mx-2 w-full max-w-4xl max-h-full bg-white shadow-lg rounded-lg px-5 overflow-hidden flex flex-col"
      >
        <div class="flex flex-col bg-white pt-5 flex-shrink-0 border-b-2">
          <div class="flex">
            <div
              class="w-4/5 text-left px-3 pb-3 text-purple-700 text-2xl font-bold"
            >
              Nueva ficha
            </div>
            <div class="w-1/5 text-right">
              <button
                aria-label="close"
                class="text-xl text-gray-500"
                @click.prevent="close"
              >
                ×
              </button>
            </div>
          </div>

          <div class="flex flex-wrap items-center justify-end mb-4">
            <button
              type="button"
              class="flex items-center px-4 py-2 rounded-full bg-teal-500 text-gray-100 hover:bg-teal-700 mb-2"
              @click="addQuestion('open')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                class="h-6 w-6 fill-current"
                viewBox="0 0 24 24"
              >
                <defs>
                  <path id="a" d="M0 0h24v24H0V0z" />
                </defs>
                <clipPath id="b">
                  <use xlink:href="#a" overflow="visible" />
                </clipPath>
                <path d="M4 9h16v2H4zm0 4h10v2H4z" clip-path="url(#b)" />
              </svg>
              <span class="ml-2 text-sm tracking wide font-bold"
                >Pregunta abierta</span
              >
            </button>
            <button
              type="button"
              class="flex items-center px-4 py-2 ml-2 rounded-full bg-indigo-500 text-gray-100 hover:bg-indigo-700 mb-2"
              @click="addQuestion('multiple')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 fill-current"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"
                />
                <path d="M0 0h24v24H0z" fill="none" />
              </svg>
              <span class="ml-2 text-sm tracking wide font-bold"
                >Pregunta de opción</span
              >
            </button>
            <button
              type="button"
              class="flex items-center px-4 py-2 ml-2 rounded-full bg-purple-500 text-gray-100 hover:bg-purple-700 mb-2"
              @click="addQuestion('multiselect')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 fill-current"
                viewBox="0 0 24 24"
              >
                <path
                  d="m10.933 13.519-2.226-2.226-1.414 1.414 3.774 3.774 5.702-6.84-1.538-1.282z"
                ></path>
                <path
                  d="M19 3H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2zM5 19V5h14l.002 14H5z"
                />
                <path d="M0 0h24v24H0z" fill="none" />
              </svg>
              <span class="ml-2 text-sm tracking wide font-bold"
                >Pregunta de selección múltiple</span
              >
            </button>
            <button
              type="button"
              class="flex items-center px-4 py-2 ml-2 rounded-full bg-orange-500 text-gray-100 hover:bg-orange-700 mb-2"
              @click="addQuestion('conditional')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 fill-current"
              >
                <path
                  d="M7.45 17.45 6.05 16.05 9.075 13H2V11H9.075L6.05 7.95L7.45 6.55L12.9 12ZM13 9V7H22V9ZM13 17V15H22V17ZM16 13V11H22V13Z"
                />
              </svg>
              <span class="ml-2 text-sm tracking wide font-bold"
                >Pregunta condicional</span
              >
            </button>
            <button
              type="button"
              class="flex items-center px-4 py-2 ml-2 rounded-full bg-pink-500 text-gray-100 hover:bg-pink-700 mb-2"
              @click="addQuestion('fields')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 fill-current"
              >
                <path d="M7 20V7H2V4H15V7H10V20ZM16 20V12H13V9H22V12H19V20Z" />
              </svg>
              <span class="ml-2 text-sm tracking wide font-bold"
                >Pregunta de campos</span
              >
            </button>
          </div>
          <p
            v-if="maximumQuestionError"
            class="text-red-500 text-xs italic pb-1"
          >
            Solo puede agregar un máximo de 8 preguntas.
          </p>
        </div>
        <div class="relative overflow-y-auto pt-4">
          <draggable
            tag="div"
            :list="questions"
            class="w-full h-full overflow-auto"
            handle=".handle"
            @end="onDragEnd"
          >
            <div
              v-for="(question, index) in questions"
              :key="question.id_pregunta"
              class="flex px-2"
            >
              <div class="self-center pr-4 cursor-move">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 fill-current handle"
                  viewBox="0 -960 960 960"
                >
                  <path
                    d="M120-200v-80h720v80H120Zm0-160v-80h720v80H120Zm0-160v-80h720v80H120Zm0-160v-80h720v80H120Z"
                  />
                </svg>
              </div>
              <open-question
                v-if="question.tipo === 'ABIERTA'"
                @click="removeQuestion(index)"
                :question="question"
                :ref="'question' + index"
              ></open-question>
              <multiple-question
                v-if="question.tipo === 'SELECCION'"
                @click="removeQuestion(index)"
                :question="question"
                :ref="'question' + index"
              ></multiple-question>
              <multiselect-question
                v-if="question.tipo === 'MULTISELECCION'"
                @click="removeQuestion(index)"
                :question="question"
                :ref="'question' + index"
              ></multiselect-question>
              <conditional-question
                v-if="question.tipo === 'SELECCION_CONDICIONAL'"
                @click="removeQuestion(index)"
                :question="question"
                :ref="'question' + index"
              ></conditional-question>
              <fields-question
                v-if="question.tipo === 'CAMPOS'"
                @click="removeQuestion(index)"
                :question="question"
                :ref="'question' + index"
              ></fields-question>
            </div>
          </draggable>
        </div>

        <div
          class="flex w-full justify-end py-5 bg-white flex-shrink-0 border-t-2"
        >
          <button
            class="ml-2 bg-purple-500 hover:bg-purple-700 px-4 py-2 text-center rounded-lg text-gray-100 tracking-wide"
            @click="showPreviewQuestions()"
          >
            Previsualización
          </button>

          <button
            class="ml-2 bg-purple-500 hover:bg-purple-700 px-4 py-2 text-center rounded-lg text-gray-100 tracking-wide"
            @click="saveQuestions()"
          >
            Guardar
          </button>
        </div>

        <project-preview-question-builder
          :showing="showProjectPreview"
          @close="showProjectPreview = false"
        ></project-preview-question-builder>
      </div>
    </div>
  </Transition>
</template>

<script>
import OpenQuestion from "./OpenQuestion";
import MultipleQuestion from "./MultipleQuestion";
import MultiselectQuestion from "./MultiselectQuestion";
import ConditionalQuestion from "./ConditionalQuestion";
import FieldsQuestion from "./FieldsQuestion";
import ProjectPreviewQuestionBuilder from "./ProjectPreviewQuestionBuilder";
import EventBus from "../event-bus";
import draggable from "vuedraggable";

export default {
  name: "project-quest-builder",
  data() {
    return {
      lastId: 1,
      backupQuestions: [],
      maximumQuestionError: false,
      showProjectPreview: false,
    };
  },
  props: {
    showing: {
      required: true,
      type: Boolean,
    },
    questions: {
      required: true,
      type: Array,
    },
    modalAction: {
      required: true,
    },
  },
  components: {
    OpenQuestion,
    MultipleQuestion,
    MultiselectQuestion,
    ConditionalQuestion,
    FieldsQuestion,
    ProjectPreviewQuestionBuilder,
    draggable,
  },
  watch: {
    showing(value) {
      if (value) {
        this.lastId = this.questions.length + 1;
        return document.querySelector("body").classList.add("overflow-hidden");
      }

      document.querySelector("body").classList.remove("overflow-hidden");
    },
  },
  mounted() {
    this.backupQuestions = JSON.parse(JSON.stringify(this.questions));
  },
  methods: {
    reset() {
      this.lastId = 1;
      this.showProjectPreview = false;
    },
    close() {
      if (this.modalAction !== "edit") {
        this.reset();
      }

      this.$emit("close", this.backupQuestions);
    },
    addQuestion(type) {
      if (this.questions.length < 8) {
        if (type === "open") {
          let question = {
            id_pregunta: this.lastId,
            tipo: "ABIERTA",
            texto: "",
            nota_para_registrante: "",
            subpregunta: [],
            obligatoria: false,
            error: false,
          };
          this.questions.push(question);
          this.lastId += 1;
        }
        if (type === "multiple") {
          let question = {
            id_pregunta: this.lastId,
            tipo: "SELECCION",
            texto: "",
            nota_para_registrante: "",
            opciones: ["", ""],
            otro: false,
            subpregunta: [],
            obligatoria: false,
            error: false,
            optionError: false,
            minimumError: false,
            maximumOptionError: false,
            maximumSubquestionError: false,
          };
          this.questions.push(question);
          this.lastId += 1;
        }
        if (type === "multiselect") {
          let question = {
            id_pregunta: this.lastId,
            tipo: "MULTISELECCION",
            texto: "",
            nota_para_registrante: "",
            opciones: ["", ""],
            otro: false,
            subpregunta: [],
            obligatoria: false,
            error: false,
            optionError: false,
            minimumError: false,
            maximumOptionError: false,
          };
          this.questions.push(question);
          this.lastId += 1;
        }
        if (type === "conditional") {
          let question = {
            id_pregunta: this.lastId,
            tipo: "SELECCION_CONDICIONAL",
            texto: "",
            nota_para_registrante: "",
            opciones: ["", ""],
            preguntas_condicion: [],
            obligatoria: false,
            error: false,
            errorCond: false,
            optionError: false,
            minimumError: false,
            maximumError: false,
            duplicateError: false,
            maximumOptionError: false,
          };
          this.questions.push(question);
          this.lastId += 1;
        }
        if (type === "fields") {
          let question = {
            id_pregunta: this.lastId,
            tipo: "CAMPOS",
            texto: "",
            nota_para_registrante: "",
            campos: [
              {
                texto: "",
                tipo: "texto",
              },
            ],
            obligatoria: false,
            error: false,
            fieldError: false,
            minimumError: false,
            maximumOptionError: false,
          };
          this.questions.push(question);
          this.lastId += 1;
        }
      } else {
        this.maximumQuestionError = true;
      }
    },
    removeQuestion(questionKey) {
      this.questions.splice(questionKey, 1);

      this.questions.forEach((question, index) => {
        question.id_pregunta = index + 1;
      });

      this.lastId = this.questions.length + 1;
      this.maximumQuestionError = false;
    },
    saveQuestions() {
      if (this.validateQuestions()) {
        this.$emit("save");
      }
    },
    validateQuestions() {
      let validateValues = [];
      Object.keys(this.$refs).forEach((el) => {
        if (this.$refs[el][0] !== undefined) {
          let value = this.$refs[el][0].validateQuestion();
          validateValues.push(value);
        }
      });

      return validateValues.every(Boolean);
    },
    showPreviewQuestions: function () {
      if (this.validateQuestions()) {
        this.showProjectPreview = true;
        let list = JSON.parse(JSON.stringify(this.questions));
        EventBus.$emit("previewQuestions", {
          questions: list,
        });
      }
    },
    onDragEnd: function (evt) {
      if (evt.newIndex !== evt.oldIndex) {
        this.questions.forEach((question, index) => {
          question.id_pregunta = index + 1;
        });
      }
    },
  },
};
</script>

<style scoped>
.bg-semi-75 {
  background-color: rgba(0, 0, 0, 0.75);
}
</style>
